import { StickableTanstackTable } from 'components/common/Table/StickableTanstackTable';
import i18n from 'i18n';
import {
  ChannelGroupAdTypeListColumns,
  useChannelGroupAdTypeListModel
} from './ChannelGroupAdTypeListModel';
import { useCallback, useState } from 'react';
import { Button } from 'react-bootstrap';
import { ChannelGroupAdTypeForm } from './ChannelGroupAdTypeForm';
import { LoadingIndicator } from 'components/common/LoadingIndicator';
import IconWithTooltip from 'components/IconWithTooltip/IconWithTooltip';
import { faPencilAlt, faTrashAlt } from '@fortawesome/free-solid-svg-icons';
import {
  CampaignAdTypeSettingFormData,
  CampaignAdTypeState
} from 'core/channelGroup/ChannelGroup';
import { compact, isEmpty } from 'lodash';

export const ChannelGroupAdTypeList: React.FC = () => {
  const {
    loading,
    columns,
    adTypeSettings,
    adTypesUsageStatus,
    adTypeToEdit,
    setAdTypeToEdit,
    deleteCampaignAdType
  } = useChannelGroupAdTypeListModel();

  const [showChannelGroupAdTypeForm, setShowChannelGroupAdTypeForm] =
    useState(false);

  const onAddChannelGroupAdTypeButtonClick = useCallback(() => {
    setShowChannelGroupAdTypeForm(true);
  }, []);

  const hideChannelGroupAdTypeForm = useCallback(() => {
    setAdTypeToEdit(undefined);
    setShowChannelGroupAdTypeForm(false);
  }, [setAdTypeToEdit]);

  const renderListOperations = useCallback(() => {
    return [
      <Button
        variant='tertiary'
        size='sm'
        key='new-btn'
        onClick={onAddChannelGroupAdTypeButtonClick}
      >
        {i18n.t('channelGroupAdTypeList.buttons.new')}
      </Button>
    ];
  }, [onAddChannelGroupAdTypeButtonClick]);

  const renderRowBtns = useCallback(
    (adType: CampaignAdTypeSettingFormData) => {
      const adTypeId = adType.adTypeId;
      if (adTypeId === undefined) {
        return [];
      }
      const onEditClickHandler = () => {
        setAdTypeToEdit(adTypeId);
      };
      const onDeleteClickHandler = () => {
        deleteCampaignAdType(adTypeId);
      };
      let cannotDeleteAdType = false;
      if (adTypesUsageStatus[adTypeId]) {
        cannotDeleteAdType = true;
      }
      return compact([
        <IconWithTooltip
          key={`edit-${adTypeId}`}
          icon={faPencilAlt}
          onClick={onEditClickHandler}
          tooltipProps={{
            id: `adTypeListEditTip-${adTypeId}`,
            tooltip: i18n.t('channelGroupAdTypeList.hints.edit')
          }}
        />,
        adType.state !== CampaignAdTypeState.DELETED ? (
          <IconWithTooltip
            key={`delete-${adTypeId}`}
            icon={faTrashAlt}
            onClick={onDeleteClickHandler}
            disabled={cannotDeleteAdType}
            tooltipProps={{
              id: `adTypeListDeleteTip-${adTypeId}`,
              tooltip: cannotDeleteAdType
                ? i18n.t('channelGroupAdTypeForm.errors.adTypeUsed', {
                    adTypes: adType.name
                  })
                : i18n.t('channelGroupAdTypeList.hints.delete')
            }}
          />
        ) : undefined
      ]);
    },
    [adTypesUsageStatus, setAdTypeToEdit, deleteCampaignAdType]
  );

  if (adTypeSettings.length > 0 && isEmpty(adTypesUsageStatus)) {
    return <LoadingIndicator />;
  }

  return (
    <>
      {loading && <LoadingIndicator />}
      <StickableTanstackTable
        customPadding={false}
        keyField={'id'}
        data={adTypeSettings}
        columns={columns}
        selectFieldName='adTypeId'
        noDataIndication={i18n.t('channelGroupAdTypeList.placeholders.noData')}
        defaultSorted={[
          {
            dataField: ChannelGroupAdTypeListColumns.ID,
            order: 'desc'
          }
        ]}
        renderListOperations={renderListOperations}
        hidePagination={true}
        renderRowBtns={renderRowBtns}
      />
      {(showChannelGroupAdTypeForm || adTypeToEdit) && (
        <ChannelGroupAdTypeForm
          adTypeId={adTypeToEdit}
          allAdTypes={adTypeSettings}
          onClose={hideChannelGroupAdTypeForm}
        />
      )}
    </>
  );
};
